import { memo, useMemo } from 'react';

import { useAtomValue } from 'jotai';

import type { Segmentation } from '@core';
import { activeData3dAtom, comparisonData3dAtom, currentFrameAtom, syncedFrameAtom, traceAtom } from '@store';
import { hexColors } from '@ui';

import { getKeypointId } from '../helpers';
import { VISUALIZATION_AXES, VISUALIZATION_MEASUREMENTS } from '../mappings';

import { MultiColorTracer, type SplitPointConfigItem, type TrackData } from './Tracer';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';

type KeypointPathProps = {
    frame?: number;
    skeleton: FrameFirstSkeleton;
    keypointIdToName: KeypointIdToName;
    segmentation: Segmentation;
    keypointName: string;
    opacity?: number;
};

export function KeypointPath({
    frame = 0,
    skeleton,
    keypointIdToName,
    segmentation,
    keypointName,
    opacity = 1,
}: KeypointPathProps) {
    const pointId = getKeypointId(keypointName, keypointIdToName);

    const derivedTrack: TrackData = useMemo(() => {
        const points: TrackData = [];
        const maxFrameToProcess = Math.min(frame, skeleton.length - 1);

        for (let i = 0; i <= maxFrameToProcess; i++) {
            const frameData = skeleton[i];
            const keypointData = frameData?.[pointId];

            if (Array.isArray(keypointData) && keypointData.length >= 3) {
                points.push([keypointData[0], keypointData[1], keypointData[2]]);
            } else {
                points.push(null);
            }
        }
        return points;
    }, [frame, skeleton, pointId]);

    const splitConfig: SplitPointConfigItem[] = useMemo(
        () => [
            {
                min: segmentation['P1'],
                max: segmentation['P4'],
                color: hexColors.blue,
                lineWidth: 0.01,
            },
            {
                min: segmentation['P4'] + 1,
                max: segmentation['P7'] - 1,
                color: hexColors.orange,
                lineWidth: 0.01,
            },
            {
                min: segmentation['P7'],
                max: segmentation['P7'] + 1,
                color: hexColors.black,
                lineWidth: 0.015,
            },
            {
                min: segmentation['P7'] + 2,
                max: segmentation['P10'],
                color: hexColors.magenta,
                lineWidth: 0.01,
            },
        ],
        [segmentation],
    );

    return <MultiColorTracer frame={frame} track={derivedTrack} splitPointConfig={splitConfig} opacity={opacity} />;
}

export const MemoKeypointPath = memo(KeypointPath);

const CURRENT = 'current';

const useTraces = () => {
    const traceMeasurementIds = useAtomValue(traceAtom);
    return useMemo(() => {
        const keypoints = traceMeasurementIds.map((id) => {
            const measurement = VISUALIZATION_MEASUREMENTS[id];
            if (!measurement) return null;
            const keypointName = VISUALIZATION_AXES[measurement.axes[0]]?.targetPoints?.[0];
            return keypointName;
        });
        return keypoints.filter((keypointName): keypointName is string => keypointName !== null);
    }, [traceMeasurementIds]);
};

export const Path = ({ origin = CURRENT }) => {
    const currentFrame = useAtomValue(origin === CURRENT ? currentFrameAtom : syncedFrameAtom);
    const data = useAtomValue(origin === CURRENT ? activeData3dAtom : comparisonData3dAtom);

    const traces = useTraces();
    if (!data) return null;

    return (
        <>
            {traces.map((trace) => (
                <MemoKeypointPath
                    frame={currentFrame}
                    skeleton={data.skeleton}
                    keypointIdToName={data.keypointIdToName}
                    segmentation={data.segmentation}
                    keypointName={trace}
                />
            ))}
        </>
    );
};
